import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoginPromptModal } from './ui/LoginPromptModal';
import type { FeedbackRelatedEntityType } from '@wineryxplorer/shared';

interface ReportErrorLinkProps {
  entityType: FeedbackRelatedEntityType;
  entityId: string;
  entityName: string;
  className?: string;
}

/**
 * A quiet "Report an error" courtesy link shown at the very bottom of the
 * winery/wine/event detail pages. Deliberately a text link and not a button:
 * buttons compete for action, and this is an offer of last resort, not
 * something we want to invite on arrival (see #270).
 * - Anonymous users → LoginPromptModal (matches the favorites/itineraries flow).
 * - Logged-in users → navigate to /report-errors with entity context as query
 *   params, where the form pre-selects "Data error" and shows a context chip.
 */
export const ReportErrorLink = ({
  entityType,
  entityId,
  entityName,
  className = '',
}: ReportErrorLinkProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const onClick = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    const params = new URLSearchParams({
      entityType,
      entityId,
      entityName,
    });
    navigate(`/report-errors?${params.toString()}`);
  };

  return (
    <>
      <p className={`font-sans text-sm text-gray-600 ${className}`}>
        Notice something wrong on this page?{' '}
        <button
          type="button"
          onClick={onClick}
          className="text-primary-700 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 rounded"
          aria-label={`Report an error on ${entityName}`}
        >
          Report an error
        </button>
      </p>
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="Reporting an error"
      />
    </>
  );
};
